import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { lastRoom, rejoinable } from "../lib/rejoin";
import { sweepOldRooms, sweepStaleRooms } from "../lib/room";
import { closeIdleRooms, isAdmin } from "../lib/admin";
import { useT } from "../i18n";

// Deletes old, empty rooms (see "Tidying up" in room.js).
export function RoomCleanup() {
  const { user } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (!user || done.current) return;
    done.current = true;
    sweepOldRooms()
      .catch(() => {})
      .then(() => sweepStaleRooms())
      .catch(() => {})
      // The owner's browser can see every room, so it closes all the idle ones.
      .then(() => isAdmin(user) && closeIdleRooms())
      .catch(() => {});
  }, [user]);
  return null;
}

// Signed-in players whose browser or computer went down mid-game: when they
// open PartyCade again (on any device) within 5 minutes, take them straight
// back to their seat.
export default function RejoinListener() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const path = useRef(pathname);
  path.current = pathname;
  const checked = useRef(new Set());
  const uid = profile?.uid;

  useEffect(() => {
    if (!uid || checked.current.has(uid)) return;
    checked.current.add(uid);
    const from = path.current;
    // Opened a room link? Then that's where they're going.
    if (from.startsWith("/r/")) return;
    // The profile still shows the room they were in before the crash (from any
    // device); this browser also remembers its last room.
    const codes = [...new Set([lastRoom(uid), profile.status?.roomCode].filter(Boolean))];
    (async () => {
      for (const code of codes) {
        const how = await rejoinable(code, uid).catch(() => null);
        if (!how) continue;
        const go = () => navigate(`/r/${code}`);
        if (how === "offline" && path.current === from) {
          go();
          toast({ emoji: "refresh", kind: "success", title: t("Welcome back!"), body: t("Taking you back to your game in room {code}.", { code }) });
        } else {
          // Still open somewhere else (or the game hasn't noticed the drop yet).
          toast({
            emoji: "gamepad",
            title: t("Your game is still going"),
            body: t("Room {code}", { code }),
            duration: 15000,
            action: { label: t("Rejoin"), onClick: go },
          });
        }
        return;
      }
    })();
  }, [uid, profile, navigate, toast, t]);

  return null;
}
